import React from 'react';

interface NumberInputProps {
  value: string | number;
  onChange: (value: string) => void;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
  disabled?: boolean;
  allowDecimals?: boolean;
  allowNegative?: boolean;
}

const NumberInput: React.FC<NumberInputProps> = ({
  value,
  onChange,
  placeholder = "0.00",
  min,
  max,
  step,
  className = "",
  disabled = false,
  allowDecimals = true,
  allowNegative = false
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Allow empty string
    if (inputValue === '') {
      onChange('');
      return;
    }
    
    // Build regex pattern based on options
    let pattern = '^';
    
    // Allow negative sign at the beginning if enabled
    if (allowNegative) {
      pattern += '-?';
    }
    
    // Allow digits
    pattern += '\\d*';
    
    // Allow decimal point and decimal digits if enabled
    if (allowDecimals) {
      pattern += '\\.?\\d*';
    }
    
    pattern += '$';
    
    const regex = new RegExp(pattern);
    
    // Only update if the input matches our pattern
    if (regex.test(inputValue)) {
      // Apply min/max constraints if specified
      if (inputValue !== '' && !isNaN(Number(inputValue))) {
        const numValue = Number(inputValue);
        
        if (min !== undefined && numValue < min) {
          onChange(min.toString());
          return;
        }
        
        if (max !== undefined && numValue > max) {
          onChange(max.toString());
          return;
        }
      }
      
      onChange(inputValue);
    }
  };

  const handleBlur = () => {
    // Convert empty string to 0 if needed when field loses focus
    // This is optional - remove if you want to keep empty strings
    if (value === '' && min !== undefined) {
      onChange(min.toString());
    }
  };

  return (
    <input
      type="text"
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      inputMode="decimal" // Shows numeric keypad on mobile
    />
  );
};

export default NumberInput; 